<template>
  <div class="mc-video-history-new__timeline-wrapper">
    <div class="mc-video-history-new__timeline-label-time-wrapper">
      <span
        v-for="(item, index) in formattedTimeLabels"
        :key="`${item.label}-${index}`"
        class="mc-video-history-new__timeline-label-time"
        :class="{
          'mc-video-history-new__timeline-label-time--first': index === 0,
          'mc-video-history-new__timeline-label-time--last':
            index === formattedTimeLabels.length - 1,
        }"
        :style="{ left: `${item.startPercentage}%` }"
      >
        {{ item.label }} {{ formatLabelTimezone(deviceTimezone) }}
      </span>
    </div>

    <div class="mc-video-history-new__timeline-alerts-wrapper">
      <div
        v-for="(item, index) in calculatedAlerts"
        :key="`timeline-alert-${index}`"
        class="mc-video-history-new__timeline-alert"
        :class="{
          'mc-video-history-new__timeline-alert-cluster': item.type === 'cluster',
        }"
        :style="{ left: `${item.startPercentage}%` }"
      >
        <span
          v-if="item.type === 'alert'"
          class="mc-video-history-new__timeline-alert-icon"
          :class="{
            urgent: item.isUrgent,
            important: item.isImportant,
          }"
          @mouseenter="handleAlertMouseEnter($event, item)"
          @mouseleave="handleAlertMouseLeave"
        >
          <i :class="item.alerts[0].icon" />
        </span>

        <div
          v-else
          class="mc-video-history-new__timeline-alert-cluster"
          :class="{
            urgent: item.isUrgent,
            important: item.isImportant,
          }"
          @mouseenter="handleAlertMouseEnter($event, item)"
          @mouseleave="handleAlertMouseLeave"
        >
          {{ item.alerts.length }}
        </div>
      </div>

      <div
        v-if="alertTooltip.visible"
        class="mc-video-history-new__alert-tooltip"
        :style="{
          left: `${alertTooltip.left}px`,
          top: `${alertTooltip.top}px`,
        }"
      >
        <div
          v-for="(line, index) in alertTooltip.lines"
          :key="`${line}-${index}`"
          class="mc-video-history-new__alert-tooltip-line"
        >
          {{ line }}
        </div>
      </div>
    </div>

    <div
      ref="timelineBar"
      class="mc-video-history-new__timeline-bar"
      @mousemove="handleTimelineHover"
      @mouseleave="handleTimelineLeave"
      @click="handleTimelineClick"
    >
      <b-skeleton v-if="isLoading" height="6px" />

      <template v-else>
        <div v-if="!timelineDisabled" class="mc-video-history-new__timeline-bar-filled-wrapper">
          <TimelineFilled
            v-for="(videoFile, index) in filelistUnion"
            :key="index"
            :widthPercentage="getPercentageWidth(videoFile.startTime, videoFile.endTime)"
            :startPercentage="getStartPositionPercentage(videoFile.startTime)"
            :startTime="getUtcDateFromLocalDate(videoFile.startTime, deviceTimezone)"
            :endTime="getUtcDateFromLocalDate(videoFile.endTime, deviceTimezone)"
            :startTimeBuffered="bufferedTimeIntersection.start"
            :endTimeBuffered="bufferedTimeIntersection.end"
          />
        </div>
        <div
          v-if="hoveredTime"
          class="mc-video-history-new__timeline-tooltip"
          :style="{ left: `${tooltipPosition}px` }"
        >
          {{ hoveredTime }}
        </div>

        <span
          class="mc-video-history-new__timeline-marker-play"
          :style="{ left: `calc(${markerPosition}% - 10px)` }"
          @pointerdown.stop.prevent="handleMarkerDragStart"
          @click.stop.prevent
        >
          <i class="mc-icon icon-outline_menu"></i>
        </span>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import TimelineFilled from './TimelineFilled.vue'
import { mapGetters, mapState } from 'vuex'
import { Helper } from '@/mixin/helper'
import { getAlertIcon } from '@/helpers/alert-icon'

moment.locale('id')

export default {
  name: 'TimelineComponent',
  components: {
    TimelineFilled,
  },
  mixins: [Helper],

  props: {
    startTime: {
      // UTC Date
      type: String,
      required: false,
      default: null,
    },
    endTime: {
      // UTC Date
      type: String,
      required: false,
      default: null,
    },
    markerPosition: {
      type: Number, // percentage
      required: true,
      default: 0,
    },
    filelistUnion: {
      type: Array,
      required: false,
      default: () => [],
    },
    countTimeLabel: {
      type: Number,
      required: false,
      default: 8,
    },
    zoomPercentage: {
      type: Number,
      required: false,
      default: 100,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    timelineDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  emits: ['clickTimelineDatetime', 'markerDragStart'],

  data() {
    return {
      hoveredTime: null,
      hoveredDatetimeUtc: null,
      tooltipPosition: 0,
      timelineWidth: 0,
      alertClusterDistance: 8,
      timelineResizeObserver: null,

      alertTooltip: {
        visible: false,
        left: 0,
        top: 0,
        lines: [],
      },
    }
  },
  computed: {
    ...mapState('videoHistory', ['rawAlerts']),
    ...mapGetters('videoHistory', ['bufferedTimeIntersection', 'deviceTimezone']),

    getStartPositionPercentage() {
      return (fileStartTime) => {
        if (!this.startTime || !this.endTime || !fileStartTime) return 0
        const utcStart = this.getUtcDateFromLocalDate(fileStartTime, this.deviceTimezone)

        const periodStart = moment(this.startTime).valueOf()
        const periodEnd = moment(this.endTime).valueOf()
        const fileStart = moment(utcStart).valueOf()

        const periodInSeconds = (periodEnd - periodStart) / 1000
        const fileOffsetInSeconds = (fileStart - periodStart) / 1000

        if (
          !Number.isFinite(periodInSeconds) ||
          !Number.isFinite(fileOffsetInSeconds) ||
          periodInSeconds <= 0
        ) {
          return 0
        }

        const startPercentage = (fileOffsetInSeconds / periodInSeconds) * 100

        return Math.min(Math.max(startPercentage, 0), 100)
      }
    },

    getPercentageWidth() {
      return (fileStartTime, fileEndTime) => {
        if (!this.startTime || !this.endTime) return 0

        const periodStartTimestamp = moment(this.startTime).valueOf()
        const periodEndTimestamp = moment(this.endTime).valueOf()
        const fileStartTimestamp = moment(fileStartTime).valueOf()
        const fileEndTimestamp = moment(fileEndTime).valueOf()

        const periodInSeconds = Math.max(0, (periodEndTimestamp - periodStartTimestamp) / 1000)

        const fileDurationInSeconds = Math.max(0, (fileEndTimestamp - fileStartTimestamp) / 1000)

        if (!periodInSeconds) return 0

        return Math.min((fileDurationInSeconds / periodInSeconds) * 100, 100)
      }
    },

    formattedTimeLabels() {
      if (!this.startTime || !this.endTime) return []

      const count = Math.max(2, this.countTimeLabel)
      const startTimestamp = moment(this.startTime).valueOf()
      const endTimestamp = moment(this.endTime).valueOf()
      const duration = endTimestamp - startTimestamp

      if (!Number.isFinite(duration) || duration <= 0) return []

      const interval = duration / (count - 1)

      return Array.from({ length: count }, (_, index) => {
        const timestamp = index === count - 1 ? endTimestamp : startTimestamp + interval * index

        return {
          label: moment
            .utc(timestamp)
            .utcOffset(Number(this.deviceTimezone) * 60)
            .format('HH:mm:ss'),
          startPercentage: (index / (count - 1)) * 100,
        }
      })
    },

    normalizedAlerts() {
      if (!this.startTime || !this.endTime) return []

      const startTimestamp = new Date(this.startTime).getTime()
      const endTimestamp = new Date(this.endTime).getTime()
      const duration = endTimestamp - startTimestamp

      if (!Number.isFinite(duration) || duration <= 0) return []

      return this.rawAlerts.reduce((result, rawAlert) => {
        const timestamp = new Date(rawAlert.createdOn).getTime()

        if (!Number.isFinite(timestamp) || timestamp < startTimestamp || timestamp > endTimestamp) {
          return result
        }

        result.push({
          timestamp,
          icon: `icon-${getAlertIcon(rawAlert.alertCode)}`,
          startPercentage: ((timestamp - startTimestamp) / duration) * 100,
          isUrgent: Boolean(rawAlert.isUrgent),
          isImportant: Boolean(rawAlert.isImportant),
          descriptionEng: rawAlert.alert.descriptionEng,
          descriptionIdn: rawAlert.alert.descriptionIdn,
          alertCode: rawAlert.alert.alertCode,
        })

        return result
      }, [])
    },

    calculatedAlerts() {
      if (!this.startTime || !this.endTime) return []

      const result = []

      this.normalizedAlerts.forEach((alert) => {
        const pixelPosition = (alert.startPercentage / 100) * this.timelineWidth
        const currentItem = result[result.length - 1]
        const previousAlert = currentItem?.alerts[currentItem.alerts.length - 1]

        const previousPixelPosition = previousAlert
          ? (previousAlert.startPercentage / 100) * this.timelineWidth
          : null

        const distance =
          previousPixelPosition === null
            ? Infinity
            : Math.abs(pixelPosition - previousPixelPosition)

        if (currentItem && this.timelineWidth && distance <= this.alertClusterDistance) {
          const previousCount = currentItem.alerts.length
          const nextCount = previousCount + 1

          currentItem.startPercentage =
            (currentItem.startPercentage * previousCount + alert.startPercentage) / nextCount

          currentItem.alerts.push(alert)
          currentItem.type = 'cluster'

          if (alert.isUrgent) {
            currentItem.isUrgent = true
            currentItem.isImportant = false
          } else if (!currentItem.isUrgent && alert.isImportant) {
            currentItem.isImportant = true
          }

          return
        }

        result.push({
          type: 'alert',
          isUrgent: alert.isUrgent,
          isImportant: alert.isImportant,
          startPercentage: alert.startPercentage,
          alerts: [alert],
        })
      })

      return result
    },
  },

  mounted() {
    this.$nextTick(() => {
      const timelineBar = this.$refs.timelineBar
      if (!timelineBar) return

      this.timelineWidth = timelineBar.getBoundingClientRect().width

      if (typeof ResizeObserver === 'undefined') return

      this.timelineResizeObserver = new ResizeObserver((entries) => {
        const entry = entries[0]
        if (!entry) return

        this.timelineWidth = entry.contentRect.width
      })

      this.timelineResizeObserver.observe(timelineBar)
    })
  },

  beforeDestroy() {
    if (this.timelineResizeObserver) {
      this.timelineResizeObserver.disconnect()
      this.timelineResizeObserver = null
    }
  },

  methods: {
    handleAlertMouseEnter(event, item) {
      const rect = event.currentTarget.getBoundingClientRect()

      this.alertTooltip.lines =
        item.type === 'cluster'
          ? this.getClusterTooltipLines(item.alerts)
          : this.getAlertTooltipLines(item.alerts[0])

      this.alertTooltip.left = rect.left + rect.width / 2
      this.alertTooltip.top = rect.top - 8
      this.alertTooltip.visible = true
    },

    handleAlertMouseLeave() {
      this.alertTooltip.visible = false
      this.alertTooltip.lines = []
    },

    getAlertTooltipLines(alert) {
      const descriptionKey = this.$i18n.locale === 'id' ? 'descriptionIdn' : 'descriptionEng'

      return [alert[descriptionKey]].filter(Boolean)
    },

    getClusterTooltipLines(alerts) {
      const descriptionKey = this.$i18n.locale === 'id' ? 'descriptionIdn' : 'descriptionEng'

      const alertMap = Object.create(null)

      alerts.forEach((alert) => {
        const current = alertMap[alert.alertCode]

        if (current) {
          current.count++
          return
        }

        alertMap[alert.alertCode] = {
          count: 1,
          description: alert[descriptionKey],
        }
      })

      return Object.values(alertMap).map(
        ({ count, description }) => `${count > 1 ? `${count} x ` : ''}${description}`
      )
    },

    getClusterPosition(cluster) {
      if (!cluster.length) return 0

      const totalPercentage = cluster.reduce((total, alert) => total + alert.startPercentage, 0)

      return totalPercentage / cluster.length
    },

    getClusterTooltip(cluster) {
      if (!cluster.length) return ''

      const locale = this.$i18n.locale

      if (cluster.length === 1) {
        return cluster[0][`description${locale === 'id' ? 'Idn' : 'Eng'}`]
      }

      let mapAlert = {}
      cluster.forEach((alert) => {
        if (!mapAlert[alert.alertCode]) {
          mapAlert[alert.alertCode] = {
            count: 1,
            description: alert[`description${locale === 'id' ? 'Idn' : 'Eng'}`],
          }
        } else {
          mapAlert[alert.alertCode].count++
        }
      })

      let res = ''
      Object.values(mapAlert).forEach((alert) => {
        res += `${alert.count > 1 ? `${alert.count}x ` : ''}${alert.description}\n`
      })

      return res
    },

    handleTimelineHover(event) {
      if (!this.startTime || !this.endTime) return

      const timelineBar = this.$refs.timelineBar
      if (!timelineBar) return

      const rect = timelineBar.getBoundingClientRect()
      if (!rect.width) return

      const cursorX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width)

      const startTimestamp = moment(this.startTime).valueOf()
      const endTimestamp = moment(this.endTime).valueOf()
      const duration = endTimestamp - startTimestamp

      if (!Number.isFinite(duration) || duration <= 0) {
        this.hoveredTime = null
        return
      }

      const tooltipHalfWidth = 75
      this.tooltipPosition = Math.min(
        Math.max(cursorX, tooltipHalfWidth),
        Math.max(rect.width - tooltipHalfWidth, tooltipHalfWidth)
      )

      const percentage = cursorX / rect.width
      const hoveredTimestamp = startTimestamp + duration * percentage

      this.hoveredDatetimeUtc = moment
        .utc(hoveredTimestamp)
        .utcOffset(Number(this.deviceTimezone) * 60)
        .toISOString()

      this.hoveredTime = moment
        .utc(hoveredTimestamp)
        .utcOffset(Number(this.deviceTimezone) * 60)
        .format('YYYY-MM-DD HH:mm:ss')
    },

    handleTimelineClick() {
      if (!this.hoveredDatetimeUtc) return

      this.$emit('clickTimelineDatetime', this.hoveredDatetimeUtc)
    },

    handleTimelineLeave() {
      this.hoveredTime = null
      this.hoveredDatetimeUtc = null
    },
    handleMarkerDragStart(event) {
      this.$emit('markerDragStart', event)
    },
  },
}
</script>
